import {
  PriceBookBranchScope,
  PriceBookStatus,
  PriceBookType,
} from "../../../types/price-book.type";
import { ALL_FILTER_VALUE, STATUS_LABEL } from "../shared/constants";

export const PRICE_BOOK_TYPE_LABEL: Record<PriceBookType, string> = {
  sell: "ขาย",
  purchase: "ซื้อ",
};

/** Theme palette each pricebook type is tinted with, so no colour is hard-coded. */
export const PRICE_BOOK_TYPE_PALETTE: Record<
  PriceBookType,
  "success" | "warning"
> = {
  sell: "success",
  purchase: "warning",
};

export const PRICE_BOOK_TYPE_OPTIONS: {
  value: PriceBookType;
  label: string;
}[] = [
  { value: "sell", label: PRICE_BOOK_TYPE_LABEL.sell },
  { value: "purchase", label: PRICE_BOOK_TYPE_LABEL.purchase },
];

/** The list filter offers the same two types plus the "no filter" sentinel. */
export const PRICE_BOOK_TYPE_FILTER_OPTIONS: {
  value: PriceBookType | typeof ALL_FILTER_VALUE;
  label: string;
}[] = [
  { value: ALL_FILTER_VALUE, label: "ทั้งหมด" },
  ...PRICE_BOOK_TYPE_OPTIONS,
];

/**
 * Narrows a pricebook type coming from the API or from a select back to the two the
 * UI can render. The column is nullable and carries legacy values, so every screen
 * has to survive a type it has no label or colour for.
 */
export const toPriceBookType = (
  value: string | null | undefined,
): PriceBookType | undefined =>
  value === "sell" || value === "purchase" ? value : undefined;

/** Shown wherever a pricebook has no type the UI knows. */
export const UNKNOWN_PRICE_BOOK_TYPE_LABEL = "ไม่ระบุ";

/** A % as typed, without padding zeros: 25 → "25%", 19.75 → "19.75%". */
export const formatDiscountPercent = (value: number): string => `${value}%`;

export const ALL_BRANCHES_LABEL = "ทุกสาขา";
const HEAD_OFFICE_ONLY_LABEL = "00-HO";
export const DRAFT_LABEL = "ร่าง";

/** How far a book reaches, from the list's counts: every branch, 00-HO alone, or N branches. */
export const formatBranchReach = (
  branchScope: PriceBookBranchScope,
  branchCount: number,
): string => {
  if (branchScope === "all") return ALL_BRANCHES_LABEL;
  return branchCount <= 1 ? HEAD_OFFICE_ONLY_LABEL : `${branchCount} สาขา`;
};

/**
 * The list's status filter: the two stored statuses plus ร่าง, which is a flag of its own
 * (`is_draft`) rather than a status, and มีตั้งเวลา (`has_schedule`: a draft apply waits for its
 * day). ใช้งาน leaves drafts out.
 */
export const DRAFT_FILTER_VALUE = "draft";
export const SCHEDULED_FILTER_VALUE = "scheduled";

export const PRICE_BOOK_STATUS_FILTER_OPTIONS: {
  value:
    | PriceBookStatus
    | typeof DRAFT_FILTER_VALUE
    | typeof SCHEDULED_FILTER_VALUE
    | typeof ALL_FILTER_VALUE;
  label: string;
}[] = [
  { value: ALL_FILTER_VALUE, label: "ทั้งหมด" },
  { value: "enable", label: STATUS_LABEL.enable },
  { value: "disable", label: STATUS_LABEL.disable },
  { value: DRAFT_FILTER_VALUE, label: DRAFT_LABEL },
  { value: SCHEDULED_FILTER_VALUE, label: "มีตั้งเวลา" },
];

/** Format the pricebook list endpoint expects for `created_from` / `created_to`. */
export const API_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Wording the drawer's status switch carries. It is deliberately longer than the
 * list's STATUS_LABEL: the switch reads as a sentence about what is happening now,
 * while a table cell only has room for the state.
 */
export const DRAWER_STATUS_LABEL: Record<PriceBookStatus, string> = {
  enable: "กำลังเปิดการใช้งาน",
  disable: "ปิดการใช้งาน",
};

/** Radius the grouping containers use, matching the theme's Paper radius. */
export const GROUP_RADIUS = 3;
